/**
 * Visual Finder: opens the Ryzo search modal from our own triggers (reference/Ryzo/).
 *
 * Ryzo has no public "open" API: its modal opens only from its own floating button
 * (.ryzo-text-search-button, rendered by the "Float text search" app embed). Ryzo binds that
 * click handler and sets the button to display:flex once its config has loaded, so that is
 * our ready signal. visual-finder.css hides Ryzo's button; we click it on the user's behalf.
 *
 * Until Ryzo is ready (or if the embed is off) <html> has no .visual-finder-ready class and
 * every trigger stays hidden, so there are never dead buttons.
 *
 * Also inserts the collection button beside Boost's in-collection search, re-adding it when
 * Boost redraws, brands the modal header, and starts Ryzo's image crop on the whole image.
 */
(() => {
  const config = document.getElementById('visual-finder-config');
  if (!config) return;

  const iconUrl = config.dataset.icon;
  const ryzoButton = () => document.querySelector('.ryzo-text-search-button');
  const isReady = () => {
    const button = ryzoButton();
    return !!button && button.style.display === 'flex';
  };

  const iconImg = (size) => `<img src="${iconUrl}" alt="" width="${size}" height="${size}">`;

  /* Modal header: Ryzo leaves #ryzo-text-search-title empty and shows its own label. */
  const brandHeader = () => {
    const title = document.getElementById('ryzo-text-search-title');
    if (!title || title.querySelector('.visual-finder-modal-title')) return;
    title.innerHTML = `
      <span class="visual-finder-modal-title"><span class="visual-finder-modal-icon">${iconImg(14)}</span>Visual Finder</span>
      <p class="visual-finder-modal-intro">Describe what you're looking for or upload a photo to find matching products. See something you like? Click the cards icon to explore similar products.</p>`;
  };

  /* Ryzo writes its query into the page URL (?q=…&page=1), also after closing. On collections
     Boost reads `q` as its in-collection search, so when the modal closes put those two
     params back to what the page had when it opened (e.g. /search?q=… keeps its term). */
  const RYZO_PARAMS = ['q', 'page'];
  let returnUrl = null;
  const restoreUrl = (original) => {
    const url = new URL(window.location.href);
    RYZO_PARAMS.forEach((key) => {
      const value = original.searchParams.get(key);
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
  };

  const watchClose = () => {
    const modal = document.getElementById('ryzoTextSearchModal');
    if (!modal || modal.dataset.visualFinderWatched) return;
    modal.dataset.visualFinderWatched = 'true';
    new MutationObserver(() => {
      if (modal.style.display !== 'none' || !returnUrl) return;
      const original = new URL(returnUrl);
      returnUrl = null;
      restoreUrl(original);
      setTimeout(() => restoreUrl(original), 300);
    }).observe(modal, { attributes: true, attributeFilter: ['style'] });
  };

  const open = () => {
    if (!isReady()) return;
    brandHeader();
    watchClose();
    returnUrl = window.location.href;
    ryzoButton().click();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-visual-finder-open]');
    if (!trigger) return;
    event.preventDefault();
    open();
  });

  /* Collection button just before Boost's in-collection search. */
  const addCollectionButton = () => {
    if (!config.dataset.collectionButton) return;
    document.querySelectorAll('.boost-sd__in-collection-search').forEach((search) => {
      const row = search.parentElement;
      if (!row || row.querySelector(':scope > .visual-finder-button')) return;
      row.classList.add('visual-finder-search-row');
      if (config.dataset.buttonPosition === 'right') row.classList.add('visual-finder-search-row--right');
      search.insertAdjacentHTML('beforebegin', `
        <button type="button" class="visual-finder-button" data-visual-finder-open>
          <span>Visual Finder</span>${iconImg(15)}
        </button>`);
    });
  };

  /* Ryzo's image search crops with cropperjs and passes no starting box, so cropperjs uses its
     default: a centred box over 80% of the image. On product shots that cuts off e.g. a floor
     lamp's shade and base, and Ryzo searches for the pole. Widen it to the whole image (or the
     largest centred box when Ryzo locks the ratio) as soon as the cropper is ready. Ryzo
     re-runs its search whenever the crop changes, and its first search is debounced, so only
     the full-image search goes out. cropperjs fires a bubbling `ready` event on the <img> and
     keeps its instance on img.cropper; if Ryzo ever drops cropperjs this simply does nothing. */
  document.addEventListener('ready', (event) => {
    const img = event.target;
    const cropper = img && img.cropper;
    if (!cropper || !img.closest('.ryzo-cropper-container')) return;
    const { naturalWidth: width, naturalHeight: height } = cropper.getImageData();
    if (!width || !height) return;
    const ratio = cropper.options.aspectRatio;
    let box = { x: 0, y: 0, width, height };
    if (ratio) {
      const boxWidth = Math.min(width, height * ratio);
      const boxHeight = boxWidth / ratio;
      box = { x: (width - boxWidth) / 2, y: (height - boxHeight) / 2, width: boxWidth, height: boxHeight };
    }
    cropper.setData(box);
  }, true);

  let queued = false;
  const onMutation = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      addCollectionButton();
      brandHeader();
    }, 50);
  };

  const markReady = () => {
    document.documentElement.classList.add('visual-finder-ready');
    addCollectionButton();
    brandHeader();
    new MutationObserver(onMutation).observe(document.body, { childList: true, subtree: true });
  };

  const waitForRyzo = () => {
    if (isReady()) return markReady();
    const button = ryzoButton();
    if (!button) return;
    const observer = new MutationObserver(() => {
      if (!isReady()) return;
      observer.disconnect();
      markReady();
    });
    observer.observe(button, { attributes: true, attributeFilter: ['style'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRyzo);
  } else {
    waitForRyzo();
  }
})();

/**
 * Trade (WPD) prices on Ryzo's cards.
 *
 * WPD shows trade prices on Boost by collecting product handles from the grid, asking the
 * theme's /search?view=wcp_discount_api template (installed by WPD; it works out every
 * product's and variant's trade price in Liquid) and rewriting the prices. WPD has no Ryzo
 * module, and Ryzo's cards carry no link or handle, so we do the same here:
 *
 * 1. Read Ryzo's own API responses (we wrap fetch, as WPD does for Boost) for each product's
 *    handle, image and variants.
 * 2. Match each card to its product by image URL (unique per product; titles are not), and to
 *    the variant whose retail price the card shows (Ryzo shows the matched variant, e.g. the
 *    brass one, not the cheapest).
 * 3. Fetch WPD's prices for those handles, 10 per request like WPD (Liquid's all_products
 *    allows 20 handles per page), and show the trade price after the struck-out retail price,
 *    the way Boost's cards do for trade customers.
 *
 * Ryzo's price nodes belong to its Vue app, so they're only hidden, never edited; ours is a
 * separate element beside them. Until the trade price is known the card's price is hidden, so
 * trade customers never see retail as their price. If WPD has no trade price for a product, or
 * the request fails, Ryzo's own price shows again.
 *
 * Only runs for customers WPD gives trade prices to (wcp_wholesale_customer in theme.liquid).
 */
(() => {
  const config = document.getElementById('visual-finder-config');
  if (!config || config.dataset.trade !== 'true' || !window.fetch) return;

  const CARD = '.ryzo-text-product-card';
  const BATCH = 10;

  const imageKey = (url) => {
    if (!url) return '';
    try {
      // Same file whatever size Ryzo asks for: drop the query (?width=…) and any _300x suffix.
      return new URL(url, window.location.href).pathname.replace(/_\d*x\d*(?=\.[a-z]+$)/i, '');
    } catch (error) {
      return '';
    }
  };

  const ryzoProducts = new Map(); // image key -> Ryzo product
  const wpdProducts = new Map(); // handle -> WPD product, or null when WPD returned nothing
  const requested = new Set();

  /* 1. Collect products from any Ryzo API response (text search, image search, similar). */
  const collect = (node, depth = 0) => {
    if (!node || typeof node !== 'object' || depth > 4) return;
    if (Array.isArray(node)) {
      node.forEach((item) => collect(item, depth + 1));
      return;
    }
    if (node.handle && node.imageurl && Array.isArray(node.variants)) {
      ryzoProducts.set(imageKey(node.imageurl), node);
      node.variants.forEach((variant) => {
        const key = imageKey(variant.imageurl);
        if (key && !ryzoProducts.has(key)) ryzoProducts.set(key, node);
      });
      return;
    }
    Object.values(node).forEach((value) => collect(value, depth + 1));
  };

  const nativeFetch = window.fetch;
  window.fetch = function visualFinderFetch(input, init) {
    const request = nativeFetch.apply(this, arguments);
    let url = '';
    try {
      url = typeof input === 'string' ? input : (input && input.url) || '';
    } catch (error) {
      url = '';
    }
    if (url.indexOf('/apps/ryzo/api/') !== -1) {
      request
        .then((response) => (response.ok ? response.clone().json() : null))
        .then((data) => {
          if (!data) return;
          collect(data);
          schedule();
        })
        .catch(() => {});
    }
    return request;
  };

  /* 3. WPD prices, in batches. */
  const fetchWpd = (handles) => {
    handles.forEach((handle) => requested.add(handle));
    const done = (products) => {
      handles.forEach((handle) => {
        if (!wpdProducts.has(handle)) wpdProducts.set(handle, null);
      });
      products.forEach((product) => wpdProducts.set(product.handle, product));
      schedule();
    };
    nativeFetch(`/search?view=wcp_discount_api&handle=${handles.map(encodeURIComponent).join(',')}`, {
      cache: 'no-cache',
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => done(JSON.parse(text.replace(/-Infinity|Infinity/g, 'null')).products || []))
      .catch(() => done([]));
  };

  const amountOf = (text) => {
    const match = String(text || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    return match ? Math.round(parseFloat(match[0]) * 100) : null;
  };

  /* Ryzo's own format (e.g. "CA$869.00"): keep its prefix, swap the number. */
  const formatLike = (sample, cents) => {
    const prefix = (String(sample).trim().match(/^[^\d]*/) || [''])[0];
    const amount = (cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${prefix}${amount}`;
  };

  /* The trade price for the variant the card shows: { price, compare } in cents, or null. */
  const tradePrice = (ryzo, wpd, shownCents) => {
    const shown = ryzo.variants.filter((variant) => Math.round(parseFloat(variant.price) * 100) === shownCents);
    const variants = wpd.variants || [];
    for (const variant of shown) {
      const id = String(variant.shopify_id || variant.id || '');
      const match = variants.find((item) => String(item.id) === id);
      if (match && match._wpd_price != null) {
        return { price: Number(match._wpd_price), compare: Number(match._wpd_compare_at_price) || 0 };
      }
    }
    // No variant match (e.g. Ryzo's price data is behind Shopify's): fall back to the product.
    if (shownCents === Math.round(parseFloat(wpd.price_min) * 100) && wpd._wpd_price_min != null) {
      return { price: Number(wpd._wpd_price_min), compare: Number(wpd._wpd_compare_at_price_min) || 0 };
    }
    return null;
  };

  const reset = (card) => {
    card.classList.remove('visual-finder-trade-pending', 'visual-finder-trade-ready');
    const ours = card.querySelector('.visual-finder-trade-price');
    if (ours) ours.remove();
  };

  /* 2. Match cards and render. */
  const update = () => {
    const wanted = [];
    document.querySelectorAll(CARD).forEach((card) => {
      const priceBox = card.querySelector('.ryzo-price');
      const img = card.querySelector('.ryzo-product-image');
      const shownEl = priceBox && (priceBox.querySelector('.current-price, .single-price') || priceBox);
      const shownText = shownEl ? shownEl.textContent.trim() : '';
      const shownCents = amountOf(shownText);
      const key = img ? imageKey(img.getAttribute('src')) : '';
      const state = `${key}|${shownText}`;

      // Ryzo reuses cards between pages and searches, so start over when either changes.
      if (card.dataset.visualFinderTrade !== state) {
        reset(card);
        card.dataset.visualFinderTrade = state;
      }
      if (card.classList.contains('visual-finder-trade-ready')) return;

      const ryzo = ryzoProducts.get(key);
      if (!priceBox || shownCents == null || !ryzo) {
        card.classList.remove('visual-finder-trade-pending');
        return; // nothing to price (e.g. "Out of stock", or a card from before we loaded)
      }

      if (!wpdProducts.has(ryzo.handle)) {
        card.classList.add('visual-finder-trade-pending');
        if (!requested.has(ryzo.handle)) wanted.push(ryzo.handle);
        return;
      }

      card.classList.remove('visual-finder-trade-pending');
      const wpd = wpdProducts.get(ryzo.handle);
      const trade = wpd && tradePrice(ryzo, wpd, shownCents);
      if (!trade || trade.price >= shownCents) return; // no trade price: Ryzo's own stays

      const compare = Math.max(trade.compare, shownCents);
      const element = document.createElement('div');
      element.className = 'visual-finder-trade-price';
      element.innerHTML = `
        <span class="visual-finder-trade-price__compare">${formatLike(shownText, compare)}</span>
        <span class="visual-finder-trade-price__trade">${formatLike(shownText, trade.price)}</span>`;
      priceBox.insertAdjacentElement('afterend', element);
      card.classList.add('visual-finder-trade-ready');
    });

    const unique = [...new Set(wanted)];
    for (let i = 0; i < unique.length; i += BATCH) fetchWpd(unique.slice(i, i + BATCH));
  };

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      update();
    }, 60);
  }

  const start = () => new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * Hide out-of-stock products by default.
 *
 * Ryzo's pop-ups have an "Available Products Only" box that hides out-of-stock products, but it
 * always starts unticked and Ryzo has no setting for its default. So we tick it, once each time
 * a pop-up opens, by clicking Ryzo's own checkbox: Ryzo then runs its own filtered search and the
 * box shows the real state, so shoppers can untick it (and "Clear all" unticks it) as usual.
 *
 * We wait for the pop-up's first results before ticking. Ryzo doesn't discard late responses
 * (whichever search answers last is what it shows), so ticking while its opening search is
 * still running could leave the box ticked over unfiltered results.
 *
 * Two pop-ups: the search pop-up (#ryzoTextSearchModal) stays in the page and is shown/hidden,
 * so we reset when it hides; the product page "find similar" pop-up (.ryzo-modal-overlay) is
 * rebuilt on every open. On mobile that one shows a pill instead of the checkbox, but the hidden
 * checkbox drives the same state, so clicking it works there too.
 *
 * Theme setting: Visual Finder → "Hide out-of-stock products by default".
 */
(() => {
  const config = document.getElementById('visual-finder-config');
  if (!config || config.dataset.availableOnly !== 'true') return;

  const POPUPS = '#ryzoTextSearchModal, .ryzo-modal-overlay';
  const CHECKBOX = '.ryzo-available-products-filter input[type="checkbox"]';
  const RESULT = '.ryzo-text-product-card';
  const DONE = 'visualFinderAvailableOnly';

  const isOpen = (popup) => popup.isConnected && window.getComputedStyle(popup).display !== 'none';
  const watched = new WeakSet();

  const check = () => {
    document.querySelectorAll(POPUPS).forEach((popup) => {
      // The search pop-up only hides on close: watch its style so the next open ticks again.
      if (popup.id === 'ryzoTextSearchModal' && !watched.has(popup)) {
        watched.add(popup);
        new MutationObserver(schedule).observe(popup, { attributes: true, attributeFilter: ['style'] });
      }
      if (!isOpen(popup)) {
        delete popup.dataset[DONE];
        return;
      }
      if (popup.dataset[DONE]) return;
      const checkbox = popup.querySelector(CHECKBOX);
      if (!checkbox || !popup.querySelector(RESULT)) return; // wait for the first results
      popup.dataset[DONE] = 'true';
      if (!checkbox.checked) checkbox.click();
    });
  };

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      check();
    }, 100);
  }

  const start = () => new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
